import { useMemo } from 'react';

import LogoMarquee from '@/components/ui/logo-marquee';
import { useAvailableIntegrations } from '@/hooks/use-integrations';
import { cn } from '@/lib/tw-merge/cn';
import type { AvailableIntegration } from '@/types/integrations';

import {
  getIntegrationMeta,
  mergeLocalIntegrations,
} from '../integration-meta';
import IntegratedWithTitle from './integrated-with-title';

interface IntegrationsLogoMarqueeProps {
  /** Tailwind gap class between logos. */
  gapClass?: string;
  /** Outer container class overrides. */
  className?: string;
  /** Seconds per full cycle. */
  durationSeconds?: number;
  /**
   * Logo colour. 'light' = white logos for dark/photo backgrounds,
   * 'dark' = black logos for light backgrounds. Defaults 'light'.
   */
  tone?: 'light' | 'dark';
  isHero?: boolean;
}

// Forces every coloured brand SVG into a single tone so the same
// asset can sit on either dark or light backgrounds.
const TONE_FILTER = {
  light: 'brightness(0) invert(1)',
  dark: 'brightness(0)',
} as const;

const HERO_MARQUEE_INTEGRATIONS: readonly AvailableIntegration[] = [
  { key: 'datamerch', name: 'DataMerch', logo: '' },
  { key: 'dialpad', name: 'Dialpad', logo: '' },
  { key: 'docusign', name: 'DocuSign', logo: '' },
  { key: 'gmail', name: 'Gmail', logo: '' },
  { key: 'hubspot', name: 'HubSpot', logo: '' },
  { key: 'moneythumb', name: 'MoneyThumb', logo: '' },
  { key: 'outlook-personal', name: 'Outlook', logo: '' },
  { key: 'salesforce', name: 'Salesforce', logo: '' },
  { key: 'sharepoint-personal', name: 'SharePoint', logo: '' },
  { key: 'slack', name: 'Slack', logo: '' },
  { key: 'smartsheet', name: 'Smartsheet', logo: '' },
  { key: 'twilio', name: 'Twilio', logo: '' },
  { key: 'wordpress', name: 'WordPress', logo: '' },
  { key: 'zoho-crm', name: 'Zoho CRM', logo: '' },
  { key: 'google-places-api', name: 'Google Places API', logo: '' },
  { key: 'opencorporates', name: 'OpenCorporates', logo: '' },
  { key: 'dnb', name: 'Dun & Bradstreet', logo: '' },
];

/**
 * Marquee of integration partner logos pulled from the live API.
 * Renders the locally-bundled SVGs in `src/assets/integrations/`
 * (set on each integration's meta entry) — wordmark + icon together
 * where available, icon-only for Gmail / Teams / Outlook / SharePoint.
 * Coloured paths are flattened to a single tone via CSS filter so the
 * same source SVG works on dark and light backgrounds.
 */
export default function IntegrationsLogoMarquee({
  gapClass = 'gap-10 md:gap-16',
  className,
  durationSeconds = 40,
  tone = 'light',
  isHero = false,
}: IntegrationsLogoMarqueeProps) {
  const { data: apiIntegrations = [] } = useAvailableIntegrations();
  const integrations = useMemo(
    () =>
      isHero
        ? HERO_MARQUEE_INTEGRATIONS
        : mergeLocalIntegrations(apiIntegrations),
    [apiIntegrations, isHero]
  );

  const withLogos = integrations.filter(i =>
    Boolean(getIntegrationMeta(i.key)?.marqueeLogo)
  );

  if (withLogos.length === 0) {
    return null;
  }

  return (
    <div className={cn('flex w-full max-w-full items-center gap-6', className)}>
      {isHero && <IntegratedWithTitle />}
      <div className="min-w-0 flex-1">
        <LogoMarquee gapClass={gapClass} durationSeconds={durationSeconds}>
          {withLogos.map(integration => (
            <MarqueeLogo
              key={integration.key}
              integration={integration}
              tone={tone}
              priority={isHero}
            />
          ))}
        </LogoMarquee>
      </div>
    </div>
  );
}

function MarqueeLogo({
  integration,
  tone,
  priority = false,
}: {
  integration: AvailableIntegration;
  tone: 'light' | 'dark';
  priority?: boolean;
}) {
  const meta = getIntegrationMeta(integration.key);
  if (!meta?.marqueeLogo) return null;

  return (
    <img
      src={meta.marqueeLogo}
      alt={integration.name}
      loading={priority ? 'eager' : 'lazy'}
      {...(priority ? { fetchpriority: 'high' } : {})}
      decoding="async"
      className="h-5 w-auto shrink-0 object-contain sm:h-7"
      style={{ filter: TONE_FILTER[tone] }}
    />
  );
}
